import { useEffect, useState, useMemo } from 'react';
import { Search, Sparkles, BookOpen, ShieldCheck, Eye, Clock, Bookmark, ArrowRight, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { blogsApi } from '@/lib/api';
import { BlogPost } from '@/lib/types';
import { linkProps } from '@/lib/router';
import { slugify } from '@/lib/format';

// Fallback high-intent guides with custom images and metadata
const FALLBACK_BLOGS: Array<{
  id: string;
  title: string;
  slug: string;
  category: string;
  featured_image: string;
  excerpt: string;
  views: number;
  readTime: string;
  standard: string;
}> = [
  {
    id: 'b_solar',
    title: 'Solar Rooftop Subsidy & Net Metering Guide India (2026): PM Surya Ghar Scheme, DISCOM Approval & ROI Calculation',
    slug: 'solar-rooftop-subsidy-pm-surya-ghar-guide-india',
    category: 'Solar & Clean Energy',
    featured_image: '/blog-images/solar-rooftop-subsidy-pm-surya-ghar-guide-india.webp',
    excerpt: 'Complete guide to PM Surya Ghar ₹78,000 subsidy, ALMM-approved DCR modules, net metering rules, and 3kW payback calculation.',
    views: 460,
    readTime: '7 min read',
    standard: 'PM Surya Ghar & MNRE'
  },
  {
    id: 'b1',
    title: 'Why Is Your AC Leaking Water? 4 Major Causes, Fixes & Cleaning Tips (2026)',
    slug: 'why-is-your-ac-leaking-water-causes-fixes',
    category: 'Home Appliances & Repair',
    featured_image: '/blog-images/why-is-your-ac-leaking-water-causes-fixes.webp',
    excerpt: 'Step-by-step troubleshooting for clogged drain lines, dirty filters, and frozen coils. Learn why air conditioners leak water and how to fix them safely.',
    views: 380,
    readTime: '6 min read',
    standard: 'BIS IS 302-2-40'
  },
  {
    id: 'b2',
    title: 'Electric Wires for Home Wiring: How to Verify BIS IS 694 & Avoid Counterfeits',
    slug: 'electric-wires-home-wiring-bis-is-694-guide',
    category: 'Electrical & Hardware',
    featured_image: '/blog-images/electric-wires-home-wiring-bis-is-694-guide.webp',
    excerpt: 'Crucial guide for house wiring in India. Learn how to verify mandatory BIS IS 694:2010 certification and spot fake wire brands using the official BIS CARE app.',
    views: 420,
    readTime: '7 min read',
    standard: 'IS 694:2010 CM/L'
  },
  {
    id: 'b3',
    title: 'AC Gas Filling Guide (2026): Types of Refrigerants (R32 vs R410A), Signs & Cost',
    slug: 'ac-gas-filling-guide-cost-refrigerant-types',
    category: 'Home Appliances & Repair',
    featured_image: '/blog-images/ac-gas-filling-guide-cost-refrigerant-types.webp',
    excerpt: 'When does your AC actually need gas refilling? Compare R32 vs R410A refrigerants, standard charging costs, and how to verify nitrogen pressure leak repairs.',
    views: 310,
    readTime: '5 min read',
    standard: 'BEE ISEER & MoEFCC'
  },
  {
    id: 'b4',
    title: 'Pest Control Services in India: CIB&RC Chemical Safety & Treatment Guide',
    slug: 'pest-control-services-india-chemical-safety-guide',
    category: 'Home Maintenance',
    featured_image: '/blog-images/pest-control-services-india-chemical-safety-guide.webp',
    excerpt: 'How to select a safe, licensed pest control service. Verify CIB&RC approved chemical formulations, herbal gels, and long-term termite protection warranties.',
    views: 345,
    readTime: '6 min read',
    standard: 'CIB&RC Registered'
  },
  {
    id: 'b5',
    title: 'AC Bill Calculation Guide India: How Much Electricity Does Your AC Really Consume?',
    slug: 'ac-electricity-bill-calculation-guide-india',
    category: 'Home Appliances & Repair',
    featured_image: '/blog-images/ac-electricity-bill-calculation-guide-india.webp',
    excerpt: 'Calculate your exact monthly AC power consumption and power bill in India with formulas, BEE 3-star vs 5-star efficiency comparisons, and power saving tips.',
    views: 290,
    readTime: '8 min read',
    standard: 'BEE Star Labeling'
  }
];

export default function BlogsExplorerPage({
  categoryFilter,
  searchQuery
}: {
  categoryFilter?: string;
  searchQuery?: string;
}) {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>(categoryFilter || 'All');
  const [search, setSearch] = useState<string>(searchQuery || '');
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());

  // Fetch blogs from Supabase
  useEffect(() => {
    let isMounted = true;
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const res = await blogsApi.getBlogs({ status: 'published', page_size: 100 });
        if (res && res.items && res.items.length > 0 && isMounted) {
          setBlogs(res.items as BlogPost[]);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn('Error fetching blogs from FastAPI:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchBlogs();
    return () => {
      isMounted = false;
    };
  }, []);

  // Combine fetched blogs with rich fallbacks to ensure full catalog coverage
  const displayBlogs = useMemo(() => {
    const combined: Array<{
      id: string;
      title: string;
      slug: string;
      category: string;
      featured_image: string;
      excerpt: string;
      views: number;
      readTime: string;
      standard: string;
      created_at?: string;
    }> = [];

    const seenSlugs = new Set<string>();

    // 1. First add Supabase records
    for (const b of blogs) {
      if (seenSlugs.has(b.slug)) continue;
      seenSlugs.add(b.slug);

      // Extract image from featured_image or first img tag in content
      let img = b.featured_image;
      if (!img) {
        const match = b.content ? b.content.match(/<img[^>]+src="([^">]+)"/) : null;
        img = match ? match[1] : `/blog-images/${b.slug}.webp`;
      }

      // Extract plain text excerpt
      const cleanText = b.content ? b.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : '';
      const excerpt = cleanText ? cleanText.slice(0, 160) + '...' : 'Comprehensive buyer guide, statutory Indian safety regulations, and verification checklists by ZhaSearch.';

      // Map to fallback metadata if present
      const fallbackMatch = FALLBACK_BLOGS.find(fb => fb.slug === b.slug);

      combined.push({
        id: b.id,
        title: b.title,
        slug: b.slug,
        category: b.category || fallbackMatch?.category || 'Home Appliances & Repair',
        featured_image: img || fallbackMatch?.featured_image || '/blog-images/why-is-your-ac-leaking-water-causes-fixes.webp',
        excerpt: fallbackMatch?.excerpt || excerpt,
        views: b.views || fallbackMatch?.views || 250,
        readTime: fallbackMatch?.readTime || '6 min read',
        standard: fallbackMatch?.standard || 'Statutory Indian Standard',
        created_at: b.created_at
      });
    }

    // 2. Add fallback catalog items if not already present
    for (const fb of FALLBACK_BLOGS) {
      if (!seenSlugs.has(fb.slug)) {
        seenSlugs.add(fb.slug);
        combined.push({
          ...fb,
          created_at: new Date('2026-08-01').toISOString()
        });
      }
    }

    return combined;
  }, [blogs]);

  // Categories list
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const b of displayBlogs) {
      if (b.category) set.add(b.category);
    }
    return ['All', ...Array.from(set)];
  }, [displayBlogs]);

  // Filtered results
  const filtered = useMemo(() => {
    return displayBlogs.filter(b => {
      const matchCat =
        activeCategory === 'All' ||
        b.category.toLowerCase() === activeCategory.toLowerCase() ||
        slugify(b.category) === slugify(activeCategory);
      const matchSearch =
        !search.trim() ||
        b.title.toLowerCase().includes(search.toLowerCase()) ||
        b.excerpt.toLowerCase().includes(search.toLowerCase()) ||
        b.category.toLowerCase().includes(search.toLowerCase()) ||
        b.standard.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [displayBlogs, activeCategory, search]);

  const heroBlog = filtered[0] || displayBlogs[0];
  const gridBlogs = filtered.length > 1 ? filtered.slice(1) : filtered;

  const toggleBookmark = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setBookmarkedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 pb-20">
      {/* 🌟 1. Header Banner */}
      <section className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white pt-12 pb-16 px-4 md:px-8 border-b border-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(#6366f1_1px,transparent_1px)] [background-size:24px_24px] opacity-10" />

        <div className="max-w-6xl mx-auto space-y-6 relative z-10 text-center sm:text-left">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
            <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs font-mono font-bold uppercase tracking-wider border border-purple-400/30 flex items-center gap-1.5 shadow-sm">
              <ShieldCheck className="h-3.5 w-3.5 text-purple-400" />
              <span>Regulatory Spine & Buyer Guides</span>
            </span>
            <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-mono font-bold uppercase tracking-wider border border-emerald-400/30">
              E-E-A-T Verified
            </span>
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black font-display tracking-tight text-white leading-tight">
              Statutory Verification & <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-300">Buyer's Guides</span>
            </h1>
            <p className="text-slate-300 text-sm sm:text-base max-w-2xl font-medium">
              Actionable Indian standards, BIS & BEE regulatory verification checklists, and transparent service cost benchmarks.
            </p>
          </div>

          {/* Search Input Bar */}
          <div className="pt-2 max-w-xl mx-auto sm:mx-0">
            <div className="relative flex items-center">
              <Search className="absolute left-4 h-5 w-5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search guides by topic, appliance, standard (e.g. IS 694, AC, Gas, Pest)..."
                className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:bg-white/15 transition shadow-lg"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3.5 text-xs text-slate-400 hover:text-white font-bold bg-white/10 px-2 py-1 rounded-lg cursor-pointer"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 🏷️ 2. Category Filter Pills */}
      <section className="bg-white border-b border-slate-200 sticky top-[72px] z-30 shadow-xs">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center gap-2 overflow-x-auto no-scrollbar" style={{ scrollbarWidth: 'none' }}>
          {categories.map(cat => {
            const isActive = activeCategory.toLowerCase() === cat.toLowerCase();
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition cursor-pointer shrink-0 ${
                  isActive
                    ? 'bg-purple-700 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {cat === 'All' ? 'All Guides' : cat}
              </button>
            );
          })}
        </div>
      </section>

      {/* 📖 3. Main Guides Explorer Content */}
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-10 space-y-12">
        {/* Featured Hero Guide Showcase (Only if no search active) */}
        {!search && heroBlog && (
          <section className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/90 shadow-md hover:shadow-lg transition">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <a
                {...linkProps(`/blog/${heroBlog.slug}`)}
                className="lg:col-span-7 aspect-[16/9] rounded-2xl overflow-hidden shadow-md group relative block bg-slate-900 border border-slate-200"
              >
                <img
                  src={heroBlog.featured_image}
                  alt={heroBlog.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 bg-purple-700/90 backdrop-blur-md text-white text-[10px] font-black uppercase px-2.5 py-1 rounded-lg shadow-md border border-purple-500/50">
                  Featured Guide
                </div>
              </a>

              <div className="lg:col-span-5 space-y-4">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded-md bg-purple-100 text-purple-800 text-[11px] font-extrabold uppercase tracking-wider">
                    {heroBlog.category}
                  </span>
                  <span className="text-xs text-slate-400 font-semibold">•</span>
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                    {heroBlog.standard}
                  </span>
                </div>

                <a {...linkProps(`/blog/${heroBlog.slug}`)} className="block group">
                  <h2 className="text-2xl sm:text-3xl font-black font-display text-slate-900 group-hover:text-purple-700 transition leading-snug">
                    {heroBlog.title}
                  </h2>
                </a>

                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed line-clamp-3">
                  {heroBlog.excerpt}
                </p>

                <div className="pt-2 flex items-center justify-between border-t border-slate-100 text-xs font-semibold text-slate-500">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1 font-bold text-slate-700">
                      <Eye className="h-3.5 w-3.5 text-purple-600" />
                      {heroBlog.views} views
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 text-slate-400" />
                      {heroBlog.readTime}
                    </span>
                    <span>•</span>
                    <span>📅 {heroBlog.created_at ? new Date(heroBlog.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Recent'}</span>
                  </div>

                  <a
                    {...linkProps(`/blog/${heroBlog.slug}`)}
                    className="inline-flex items-center gap-1.5 text-xs font-extrabold text-purple-700 hover:text-purple-800 group"
                  >
                    <span>Read Guide</span>
                    <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 📚 4. Recent Guides Grid (Matching Screenshot Reference Style) */}
        <section className="space-y-6">
          <div className="flex items-center justify-between border-b border-slate-200 pb-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-black font-display text-slate-900">
                {search ? `Search Results for "${search}"` : 'Recent Buyer & Regulatory Guides'}
              </h2>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                Browse verified checklists, chemical safety norms, and technical repair standards.
              </p>
            </div>
            <span className="text-xs font-black text-purple-700 bg-purple-50 px-3 py-1.5 rounded-full border border-purple-200">
              {filtered.length} {filtered.length === 1 ? 'Guide' : 'Guides'} Available
            </span>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-slate-200 space-y-3">
              <div className="text-3xl">🔍</div>
              <h3 className="text-base font-extrabold text-slate-900">No matching guides found</h3>
              <p className="text-xs text-slate-500">Try searching with a different keyword or view all guides.</p>
              <button
                onClick={() => {
                  setSearch('');
                  setActiveCategory('All');
                }}
                className="px-4 py-2 rounded-xl bg-purple-700 text-white font-bold text-xs shadow-sm cursor-pointer"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {filtered.map(blog => {
                const isBookmarked = bookmarkedIds.has(blog.id);
                return (
                  <article
                    key={blog.id}
                    className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs hover:shadow-md transition flex flex-col justify-between overflow-hidden group h-full"
                  >
                    <div>
                      {/* Featured Image Container with Bookmark Pill */}
                      <a
                        {...linkProps(`/blog/${blog.slug}`)}
                        className="aspect-[16/9] w-full block bg-slate-900 overflow-hidden relative"
                      >
                        <img
                          src={blog.featured_image}
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <button
                          onClick={e => toggleBookmark(blog.id, e)}
                          className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-white/90 backdrop-blur-md shadow-md flex items-center justify-center text-slate-700 hover:text-purple-600 transition cursor-pointer"
                          title={isBookmarked ? 'Remove Bookmark' : 'Bookmark Guide'}
                        >
                          <Bookmark className={`h-3.5 w-3.5 ${isBookmarked ? 'fill-purple-600 text-purple-600' : ''}`} />
                        </button>

                        <div className="absolute bottom-2 left-2 bg-slate-950/80 backdrop-blur-md text-white text-[9px] font-mono font-bold px-2 py-0.5 rounded border border-white/20">
                          {blog.standard}
                        </div>
                      </a>

                      {/* Card Body */}
                      <div className="p-4 space-y-2.5">
                        <div className="flex items-center justify-between text-[10px] font-extrabold uppercase text-purple-700">
                          <span>{blog.category}</span>
                          <span className="text-slate-400 font-semibold">{blog.readTime}</span>
                        </div>

                        <a {...linkProps(`/blog/${blog.slug}`)} className="block group/title">
                          <h3 className="text-sm font-black text-slate-900 group-hover/title:text-purple-700 transition leading-snug line-clamp-2">
                            {blog.title}
                          </h3>
                        </a>

                        <p className="text-xs text-slate-500 font-medium leading-relaxed line-clamp-2">
                          {blog.excerpt}
                        </p>
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="p-4 pt-0 border-t border-slate-100 mt-2 flex items-center justify-between text-[11px] font-semibold text-slate-500">
                      <span className="flex items-center gap-2 font-bold text-slate-700">
                        <span className="flex items-center gap-1">
                          <Eye className="h-3.5 w-3.5 text-purple-600" />
                          {blog.views} views
                        </span>
                        <span>•</span>
                        <span>📅 {blog.created_at ? new Date(blog.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Recent'}</span>
                      </span>
                      <a
                        {...linkProps(`/blog/${blog.slug}`)}
                        className="text-purple-700 font-extrabold hover:underline flex items-center gap-0.5"
                      >
                        <span>Read</span>
                        <ArrowRight className="h-3 w-3" />
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
